import React, {useEffect, useRef, useState} from "react";
import {router} from "@inertiajs/react";
import classNames from "classnames";
import Svg from "@/Components/Svg";
import {useSearchStore} from "@/Stores/Site/Search/SearchStore";
import {useModalStore} from "@/Stores/Site/ModalStore";
import {
    selectSetSearchQuery,
    selectGetSearchData,
    selectOnBlur,
    selectOnChange,
    selectOnFocus,
    selectSetSearchType,
    selectSetSearchQueryOutput
} from "@/Stores/Site/Search/Selectors";
import useSearchHistory from "@/Hooks/useSearchHistory";
import {useSearchNavigation} from "@/Hooks/useSearchNavigation";
import Link from "@/Layouts/Link";
import Picture from "@/Components/Picture";
import {useDebounce} from "@/Hooks/useDebounce";
import isEmpty from "is-empty";
import "@/Scss/Components/searchForm.scss";

const SearchForm = (props) => {
    const {stores = {}, user = {}, from = ''} = props;
    const {modalStore = {}} = stores;
    const {is_barcode = 0} = user;

    const inputRef = useRef(null);

    const [suggestions, setSuggestions] = useState([]);

    const {history = [], addSearchTerm, clearHistory} = useSearchHistory();

    const suggestionsRefs = {};

    const closeSearchModal = useModalStore(state => state.closeSearchModal);
    const searchQuery = useSearchStore(state => state.searchQuery);
    const searchType = useSearchStore(state => state.searchType);
    const page = useSearchStore(state => state.page);
    const perPage = useSearchStore(state => state.perPage);
    const filterParams = useSearchStore(state => state.filterParams);
    const staticFilterParams = useSearchStore(state => state.staticFilterParams);
    const sort = useSearchStore(state => state.sort);
    const hint = useSearchStore(state => state.hint);
    const searchStoreGetSearchData = useSearchStore(selectGetSearchData);
    const searchStoreSetSearchQuery = useSearchStore(selectSetSearchQuery);
    const searchStoreSetSearchType = useSearchStore(selectSetSearchType);
    const searchStoreSetSearchQueryOutput = useSearchStore(selectSetSearchQueryOutput);
    const searchStoreOnChange = useSearchStore(selectOnChange);
    const searchStoreOnFocus = useSearchStore(selectOnFocus);
    const searchStoreOnBlur = useSearchStore(selectOnBlur);
    const resetParametersBeforeRequest = useSearchStore(state => state.resetParametersBeforeRequest);

    useEffect(() => {
        if (searchType === 'suggestions') {
            debouncedSearch();
        }
    }, [searchQuery]);

    const getSuggestions = async () => {
        const response = await axios.post('/search', {
            queryString: searchQuery,
            searchType: searchType,
            page: page,
            perPage: perPage,
            filterParams: !isEmpty(filterParams) ? filterParams : staticFilterParams,
            sort: sort,
        });

        const {success} = response.data;
        const {data = []} = success;
        const {searchSuggestions = []} = data;
        setSuggestions(searchSuggestions);
    };

    const [state, setState] = useState({
        valueType: 'search', // Тип поисковых данных (для понимания как обработать действие, выполнить запрос на поиск или перейти в деталку)
        link: '', // Ссылка
        cursorHistory: -1, //
        cursorSuggestions: 0, //
    });

    // Дебаунсим только вызов API, а не само изменение состояния
    const debouncedSearch = useDebounce(() => {
        if (searchQuery) {
            // Запрос на сервер
            getSuggestions();
        }
    }, 500);

    /**
     * Обработка ввода данных
     * @param e
     */
    const onChange = (e) => {
        searchStoreOnChange(e);
    };

    /**
     * Навигация по подсказкам
     * @type {{handleKeyDown: function(*): void}}
     */
    const suggestionsNav = useSearchNavigation({
        items: suggestions,
        cursor: state.cursorSuggestions,
        setCursor: (cursorSuggestions) =>
            setState((prev) => ({...prev, cursorSuggestions})),
        onSelect: (item, index) => {
            const href = suggestionsRefs.current[`searchSuggestions_${index}`]?.getAttribute('href') || '';
            setState((prev) => ({...prev, link: href, valueType: 'suggestions'}));
        },
    });

    /**
     * Навигация по истории
     * @type {{handleKeyDown: function(*): void}}
     */
    const historyNav = useSearchNavigation({
        items: history,
        cursor: state.cursorHistory,
        setCursor: (cursorHistory) =>
            setState((prev) => ({...prev, cursorHistory})),
        onSelect: (_, index) => {
            setState((prev) => ({...prev, link: '', valueType: 'search'}));
            searchStoreSetSearchQuery(history[index]);
        },
    });

    /**
     * Событие срабатывает при отпускании клавиши на клавиатуре
     * Перемещаемся по списку стрелками на клавиатуре
     * @param e
     * @returns {boolean}
     */
    const onKeyUp = (e) => {
        if (e.key === 'Enter') {
            // Снимаем фокусировку
            onBlur();
            // Закрываем модалку
            closeSearchModal();
            if (state.valueType === 'suggestions' && state.link !== '') {
                router.visit(state.link);
            } else if (state.valueType === 'search') {
                // Запускаем поиск
                startSearch();
            }
        } else if (suggestions.length > 0) {
            suggestionsNav.handleKeyDown(e);
        } else if (history.length > 0) {
            historyNav.handleKeyDown(e);
        }
    };

    /**
     * Событие фокус у поля поиска
     * @param e
     */
    const onFocus = (e) => {
        searchStoreOnFocus(e);
        inputRef.current.focus();
    };

    /**
     * Потеря фокуса у поля поиска
     * @param e
     */
    const onBlur = (e) => {
        setTimeout(() => {
            searchStoreOnBlur(e);
        }, 500);
    };

    /**
     * Запустить поиск (нажатие кнопки, запрос на сервер)
     */
    const startSearch = () => {
        // Сброс параметров
        resetParametersBeforeRequest();
        // Добавляем в историю запросов
        addSearchTerm(searchQuery);
        // Меняем тип поиска
        searchStoreSetSearchType('search');
        // Запоминаем поисковую фразу
        searchStoreSetSearchQueryOutput();
        // Запрос на сервер
        // getSearch();
        searchStoreGetSearchData();
    };

    /**
     * Обработка истории запросов
     * @param item
     */
    const onClickHistory = (item) => {
        // Закрываем модалку
        closeSearchModal();
        // Добавляем фразу (на которую нажали) в поисковой запрос
        searchStoreSetSearchQuery(item);
        // Запускаем поиск
        startSearch();
    };

    /**
     * Удалить поисковой запрос
     */
    const deleteSearchQuery = () => {
        searchStoreSetSearchQuery();
        onFocus();
    };

    /**
     * Очистить историю
     */
    const deleteHistory = () => {
        clearHistory();
    };

    return (
        <div className={`searchForm`}>
            <div className={`searchForm__container`}>
                {from === 'modal' && (
                    <div className={`searchForm__crossContainer`} onClick={closeSearchModal}>
                        <Svg id={`arrows-left-product`} className={`svg--cross`} />
                    </div>
                )}
                <div className={`searchForm__fieldContainer`}>
                    <div className={`searchForm__field`}>
                        <input ref={inputRef}
                               name={`search`}
                               type={`text`}
                               placeholder={`Найти в каталоге`}
                               value={searchQuery}
                               autoComplete={`off`}
                               onChange={onChange}
                               onKeyUp={onKeyUp}
                               onFocus={onFocus}
                               onBlur={onBlur}
                               className={`searchForm__input`}
                               autoFocus={from === 'modal'}
                        />
                    </div>
                    {searchQuery !== '' && (
                        <div
                            className={`searchForm__crossContainer`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={deleteSearchQuery}
                        >
                            <Svg id={`cross`} className={`svg--cross`} />
                        </div>
                    )}
                    {is_barcode === 1 && searchQuery === '' && (
                        <div className={`searchForm__barcodeReader`} onClick={modalStore.openModalScanner}>
                            <Svg id={`barcodeReader`} className={`svg--barcodeReader`} />
                        </div>
                    )}
                    <div className={`searchForm__button`} onClick={startSearch}>
                        <Svg id={`search`} className={`svg--searchPage`} />
                    </div>
                </div>
            </div>
            {hint && (
                <div className={classNames('searchForm__hint', {'searchForm__hint--modal': from === 'modal'})}>
                    <div className={`searchForm__emptyBlock`}></div>
                    <div
                        className={classNames('searchForm__scrollableBlock', {
                            'searchForm__scrollableBlock--modal': from === 'modal',
                        })}
                    >
                        {history.length > 0 && suggestions.length === 0 && (
                            <div className={`searchForm__history`}>
                                <div className={`searchForm__historyHead`}>
                                    <p>История</p>
                                    <p className={`clear`} onClick={deleteHistory}>Очистить</p>
                                </div>
                                <div className={`searchForm__historyBody`}>
                                    {history.map((item, index) => {
                                        return <div
                                            key={index}
                                            onClick={() => onClickHistory(item)}
                                            className={`searchForm__historyItem${state.cursorHistory === index ? ' focused' : ''}`}
                                        >
                                            <div className={`searchForm__historySvg`}>
                                                <Svg id={`searchHistory`} className={`svg--searchHistory`} />
                                            </div>
                                            <p>{item}</p>
                                        </div>
                                    })}
                                </div>
                            </div>
                        )}
                        {suggestions.length > 0 && (
                            <div className={`searchForm__suggestions`}>
                                {suggestions.map((el, index) => {
                                    return <Link
                                        key={index}
                                        href={`${el.url}`}
                                        ref={ref => (suggestionsRefs[`searchSuggestions_${index}`] = ref)}
                                        className={`searchForm__suggestionItem${state.cursorSuggestions === index ? ' focused' : ''}`}
                                    >
                                        <div className={`searchForm__suggestionIcon`}>
                                            {el.img === 'catalog'
                                                ? <Svg id={`catalog`} className={`svg--searchSuggestions`} />
                                                : <Picture src={el.img} alt={el.h1} />
                                            }
                                        </div>
                                        <p>{el.h1}</p>
                                    </Link>
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SearchForm;
